// RankingCompareModalV2 공통 유틸 (순수 함수)

export const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const clampColorChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

export const darkenHex = (hex, ratio = 0.3) => {
  const color = String(hex || '').trim().replace('#', '');
  const safe = color.length === 3 ? color.split('').map((ch) => `${ch}${ch}`).join('') : color;
  if (!/^[0-9a-fA-F]{6}$/.test(safe)) return '#334155';

  const r = parseInt(safe.slice(0, 2), 16);
  const g = parseInt(safe.slice(2, 4), 16);
  const b = parseInt(safe.slice(4, 6), 16);
  const factor = 1 - Math.max(0, Math.min(0.9, ratio));

  const rr = clampColorChannel(r * factor).toString(16).padStart(2, '0');
  const gg = clampColorChannel(g * factor).toString(16).padStart(2, '0');
  const bb = clampColorChannel(b * factor).toString(16).padStart(2, '0');
  return `#${rr}${gg}${bb}`;
};

// 흰색 쪽으로 섞어 밝게 (최근8주 라인을 실거래 색의 밝은 변형으로)
export const lightenHex = (hex, ratio = 0.35) => {
  const color = String(hex || '').trim().replace('#', '');
  const safe = color.length === 3 ? color.split('').map((ch) => `${ch}${ch}`).join('') : color;
  if (!/^[0-9a-fA-F]{6}$/.test(safe)) return '#94a3b8';

  const r = parseInt(safe.slice(0, 2), 16);
  const g = parseInt(safe.slice(2, 4), 16);
  const b = parseInt(safe.slice(4, 6), 16);
  const t = Math.max(0, Math.min(0.9, ratio));
  const mix = (c) => clampColorChannel(c + (255 - c) * t);

  const rr = mix(r).toString(16).padStart(2, '0');
  const gg = mix(g).toString(16).padStart(2, '0');
  const bb = mix(b).toString(16).padStart(2, '0');
  return `#${rr}${gg}${bb}`;
};

export const normalizeTarget = (value = {}) => {
  const code = String(value?.code || value?.naverDanjiCode || '').trim();
  if (!code) return null;
  return {
    code,
    ptpNo: String(value?.ptpNo || value?.ptp_no || '').trim(),
    planTypeName: String(value?.planTypeName || value?.plan_type_name || '').trim(),
    planTypeName2: String(value?.planTypeName2 || value?.plan_type_name2 || '').trim(),
    pyeongLabel: String(value?.pyeongLabel || '').trim(),
    name: String(value?.name || value?.buildingName || code).trim() || code,
    address: value?.address || value?.addressRoad || value?.roadAddress || value?.jibunAddress || '',
    familyCnt: value?.familyCnt ?? value?.householdCount ?? value?.households ?? null,
    useApproveDate: value?.useApproveDate || value?.useApprovalDate || '',
    // 단지비교 표용: 적현율/적고가 (랭킹 행 값 통과)
    jeokHyunRate: value?.jeokHyunRate ?? value?.jeok_hyun_rate ?? null,
    properHighPrice: value?.properHighPrice ?? value?.proper_high_price ?? null,
  };
};

export const toDateKey = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{8}$/.test(text)) return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  return text.replace(/\./g, '-').slice(0, 10);
};

export const toTimestamp = (value) => {
  const key = toDateKey(value);
  if (!key) return Number.NEGATIVE_INFINITY;
  const time = new Date(key).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const formatNumber = (value) => {
  const num = toNumber(value);
  return num == null ? '-' : Number(num).toLocaleString('ko-KR');
};

export const formatPercent = (value) => {
  const num = toNumber(value);
  return num == null ? '-' : `${num.toFixed(1)}%`;
};

// 만원 단위 값을 한글 금액으로 (예: 325000 -> 32억5,000만원). 구간비교 표용.
export const formatKrwKorean = (value) => {
  const num = toNumber(value);
  if (num == null) return '-';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(Math.round(num));
  const eok = Math.floor(abs / 10000);
  const man = abs % 10000;
  if (eok > 0 && man > 0) return `${sign}${eok.toLocaleString('ko-KR')}억${man.toLocaleString('ko-KR')}만원`;
  if (eok > 0) return `${sign}${eok.toLocaleString('ko-KR')}억원`;
  return `${sign}${man.toLocaleString('ko-KR')}만원`;
};

export const formatKrwInManWonShort = (value) => {
  const num = toNumber(value);
  if (num == null) return '-';

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 10000) {
    const eok = abs / 10000;
    const text = Number.isInteger(eok) ? String(eok) : eok.toFixed(1).replace(/\.0$/, '');
    return `${sign}${text}억`;
  }
  return `${sign}${Math.round(abs).toLocaleString('ko-KR')}만`;
};

// 만원 단위 값 → 한글 억 표기 (소수 1자리, '12.5억' / '13억' / '5,000만')
// RankingComplexDetailModalV2(RealTransactionHybridCard.formatPrice) 와 동일한 톤
export const formatKrwEokFromManwon = (value) => {
  const num = toNumber(value);
  if (num == null) return '-';
  const sign = num < 0 ? '-' : '';
  const absMan = Math.abs(Math.round(num)); // 만원 단위
  if (absMan >= 10000) {
    const eok = absMan / 10000;
    const text = Number.isInteger(eok) ? String(eok) : eok.toFixed(1).replace(/\.0$/, '');
    return `${sign}${text}억`;
  }
  return `${sign}${absMan.toLocaleString('ko-KR')}만`;
};

export const getEndLabelStyle = (text, color) => ({
  show: true,
  formatter: () => text,
  color,
  fontSize: 11,
  fontWeight: 700,
  padding: [2, 6],
  backgroundColor: 'rgba(255,255,255,0.92)',
  borderColor: color,
  borderWidth: 1,
  borderRadius: 4,
});

const DEAL_TYPE_LABELS = { 1: '매매', 2: '전세', 3: '월세' };
export const getDealTypeLabel = (dealType) => DEAL_TYPE_LABELS[Number(dealType)] || '-';

export const fillContinuousValues = (values = []) => {
  const firstValid = values.find((value) => value != null);
  if (firstValid == null) return values;

  let carry = firstValid;
  return values.map((value) => {
    if (value == null) return carry;
    carry = value;
    return value;
  });
};

export const formatUseApproveDate = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, '');

  if (digits.length >= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  if (digits.length >= 6) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
  return raw;
};

export const formatFullDateKorean = (value) => {
  const key = toDateKey(value);
  if (!key) return '-';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return key;
  return `${key.slice(0, 4)}년 ${key.slice(5, 7)}월 ${key.slice(8, 10)}일`;
};

export const buildPaddedAxisRange = (values = [], options = {}) => {
  const { clampMinZero = false, minSpanRatio = 0.08, padRatio = 0.12 } = options;
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length === 0) return {};

  const minValue = Math.min(...finiteValues);
  const maxValue = Math.max(...finiteValues);
  const base = Math.max(Math.abs(minValue), Math.abs(maxValue), 1);
  const span = maxValue - minValue;
  const targetSpan = Math.max(span * (1 + (padRatio * 2)), base * minSpanRatio);
  const center = (minValue + maxValue) / 2;

  let nextMin = center - (targetSpan / 2);
  let nextMax = center + (targetSpan / 2);

  if (clampMinZero && nextMin < 0) {
    nextMax -= nextMin;
    nextMin = 0;
  }

  if (nextMax <= nextMin) nextMax = nextMin + 1;
  return { min: nextMin, max: nextMax };
};

export const aggregateWeeklyRows = (rows = []) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const date = toDateKey(row?.baseDate);
    if (!date) return;
    if (!grouped.has(date)) grouped.set(date, []);
    grouped.get(date).push(row);
  });

  const PICK_KEYS = [
    'salePrice', 'saleRealPrice', 'leasePrice', 'leaseRealPrice',
    'saleCount', 'leaseCount', 'saleRealCount', 'leaseRealCount',
  ];

  return Array.from(grouped.entries())
    .map(([baseDate, sameDateRows]) => {
      const result = { baseDate };
      PICK_KEYS.forEach((key) => {
        const nums = sameDateRows
          .map((row) => toNumber(row?.[key]))
          .filter((num) => num !== null);
        result[key] = nums.length === 0 ? null : nums.reduce((acc, num) => acc + num, 0) / nums.length;
      });
      return result;
    })
    .sort((a, b) => toTimestamp(a.baseDate) - toTimestamp(b.baseDate));
};

// 옵션 객체의 순환 참조를 잘라낸다(함수는 참조 보존).
// ECharts 는 함수 formatter 와 무관하게 기본 툴팁 마크업 트리(buildSection)를 항상 재귀로 빌드하는데,
// 옵션 어딘가에 순환 참조가 있으면 이 재귀가 무한히 돌아 "Maximum call stack size exceeded" 로 죽는다.
// (트레이스의 addCommas/encodeHTML 은 스택이 꽉 찬 뒤 우연히 터지는 leaf 일 뿐, 진짜 원인은 순환 참조.)
export function breakCircularRefs(input) {
  let cut = false;
  const seen = new WeakSet();
  const walk = (value) => {
    // 원시값/함수는 그대로 둔다 (함수: 참조 보존 — formatter 등)
    if (value == null || typeof value !== 'object') return value;
    if (seen.has(value)) { cut = true; return undefined; }
    seen.add(value);
    let result;
    if (Array.isArray(value)) {
      result = value.map(walk);
    } else {
      result = {};
      Object.keys(value).forEach((key) => { result[key] = walk(value[key]); });
    }
    seen.delete(value); // 형제 분기에서의 정상 공유(DAG)는 허용, 진짜 순환만 컷
    return result;
  };
  return { output: walk(input), cut };
}

// ECharts 기본 툴팁 마크업용 안전 valueFormatter.
// ECharts 는 함수 formatter 가 있어도 내부적으로 기본 마크업(buildTooltipMarkup)을 항상 빌드하며,
// 이때 기본 valueFormatter 가 addCommas() 정규식을 호출한다. 비정상적으로 긴 숫자 문자열이
// 들어오면 정규식 catastrophic backtracking 으로 스택 오버플로가 발생하므로,
// tooltip.valueFormatter 를 주입해 그 경로 자체를 우회한다. (실제 표기는 tooltip.formatter 가 담당)
export const safeTooltipValueFormatter = (value) => {
  const picked = Array.isArray(value) ? value[value.length - 1] : value;
  const num = toNumber(picked);
  return num == null ? '' : Number(num).toLocaleString('ko-KR');
};

// dataZoom 동기화 설정 — syncedZoom 상태를 inside dataZoom 에 반영
export const buildSyncedDataZoom = (syncedZoom, prefix = 'ranking-compare') => ([
  {
    type: 'inside',
    xAxisIndex: [0],
    // 휠 줌은 Ctrl+휠(포인터 중심). 일반 왼쪽 드래그는 ECharts 네이티브 pan(moveOnMouseMove).
    // (Ctrl+왼쪽 드래그는 브러시=구간비교 — 브러시 커서가 켜지면 드래그를 브러시가 가져감)
    zoomOnMouseWheel: 'ctrl',
    moveOnMouseWheel: false,
    moveOnMouseMove: true,
    // 'filter' 는 창 밖 포인트를 series 에서 제거해 connectNulls 라인이 창 경계/빈 구간에서 끊김.
    // 'none' 으로 두면 포인트를 유지 → 라인이 표시 기간 안에서 계속 이어짐(금액 없는 주는 선만, 있는 주는 동그라미).
    filterMode: 'none',
    id: `${prefix}-dataZoomX`,
    ...(syncedZoom?.start !== undefined ? { start: syncedZoom.start } : {}),
    ...(syncedZoom?.end !== undefined ? { end: syncedZoom.end } : {}),
    ...(syncedZoom?.startValue !== undefined ? { startValue: syncedZoom.startValue } : {}),
    ...(syncedZoom?.endValue !== undefined ? { endValue: syncedZoom.endValue } : {}),
  },
]);
